import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { useBlogPost } from '../../utils/blogLoader';

const components = {
  h1: ({ children, ...props }) => <h1 className="text-4xl font-bold mt-8 mb-4" {...props}>{children}</h1>,
  h2: ({ children, ...props }) => <h2 className="text-3xl font-bold mt-8 mb-4" {...props}>{children}</h2>,
  h3: ({ children, ...props }) => <h3 className="text-2xl font-bold mt-6 mb-3" {...props}>{children}</h3>,
  p: (props) => <p className="text-gray-600 leading-7 mb-4" {...props} />,
  a: ({ children, ...props }) => <a className="text-[#6C63FF] hover:underline" {...props}>{children}</a>,
  ul: (props) => <ul className="list-disc pl-6 mb-4" {...props} />,
  ol: (props) => <ol className="list-decimal pl-6 mb-4" {...props} />,
  li: (props) => <li className="text-gray-600 mb-2" {...props} />,
  blockquote: (props) => (
    <blockquote className="border-l-4 border-[#6C63FF] pl-4 italic my-4" {...props} />
  ),
  code: (props) => (
    <code className="bg-gray-100 rounded px-2 py-1 text-sm" {...props} />
  ),
  pre: (props) => (
    <pre className="bg-gray-100 rounded p-4 overflow-x-auto my-4" {...props} />
  ),
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = useBlogPost(slug);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white py-24 sm:py-32"
    >
      <Helmet>
        <title>{post.title} - RailTech Blog</title>
        <meta name="description" content={post.excerpt} />
      </Helmet>

      <article className="mx-auto max-w-3xl px-6 lg:px-8">
        <header className="mb-12">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            {post.title}
          </h1>
          <div className="mt-4 flex items-center gap-x-4 text-sm">
            <time dateTime={post.date} className="text-gray-500">
              {new Date(post.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </time>
            <span className="relative z-10 rounded-full bg-gray-50 px-3 py-1.5 font-medium text-gray-600">
              {post.category}
            </span>
          </div>
          <div className="mt-4">
            <p className="text-lg leading-8 text-gray-600">{post.excerpt}</p>
          </div>
        </header>

        <div className="prose prose-lg max-w-none">
          <ReactMarkdown components={components}>
            {post.content}
          </ReactMarkdown>
        </div>
      </article>
    </motion.div>
  );
}
